import React, { createContext, useCallback, useMemo } from 'react';

import type {
  Auth0ProviderOptions,
  WithAuthenticationRequiredOptions,
} from '@auth0/auth0-react';
import { Auth0Provider, withAuthenticationRequired } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

import { env } from '@packages/utils/env';

import { AuthContentRoot } from './auth-content-root/auth-content-root';
import { NoAuthProvider } from './no-auth-provider';

export type AuthContextValue = {
  fetchApi: WindowOrWorkerGlobalScope['fetch'];
  logout: () => void;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export type AuthProviderProps = React.PropsWithChildren<{
  auth0Config?: Auth0ProviderOptions;
  logoutURL?: string;
  onAuthRedirect: JSX.Element;
}>;

const AUTH0_CONFIG: Auth0ProviderOptions = {
  authorizationParams: {
    audience: env.VITE_RTK_AUTH0_AUDIENCE,
    redirect_uri: window.location.origin,
  },
  cacheLocation: (window as any).Cypress ? 'localstorage' : 'memory',
  clientId: env.VITE_RTK_AUTH0_CLIENT_ID,
  domain: env.VITE_RTK_AUTH0_DOMAIN,
  useRefreshTokens: true,
};

export function RtkAuthProvider({
  children,
  auth0Config = AUTH0_CONFIG,
  logoutURL,
  onAuthRedirect,
}: AuthProviderProps) {
  const navigate = useNavigate();

  const onRedirectCallback = useCallback(
    (appState: any) => {
      if (appState?.returnTo) {
        navigate(
          `${appState.returnTo}${
            appState.locationHash ? appState.locationHash : ''
          }`,
          { replace: true },
        );
      }
    },
    [navigate],
  );

  const AuthenticatedContentRoot = useMemo(() => {
    const loginConfig: WithAuthenticationRequiredOptions = {
      loginOptions: {
        appState: {
          locationHash: window.location.hash,
          returnTo: window.location.pathname,
        },
      },
      onRedirecting: () => onAuthRedirect,
    };

    return withAuthenticationRequired(AuthContentRoot, loginConfig);
  }, [onAuthRedirect]);

  if (env.MODE === 'e2e') {
    return (
      <NoAuthProvider
        auth0Config={auth0Config}
        logoutURL={logoutURL}
        onAuthRedirect={onAuthRedirect}
      >
        {children}
      </NoAuthProvider>
    );
  }

  return (
    <Auth0Provider {...auth0Config} onRedirectCallback={onRedirectCallback}>
      <AuthenticatedContentRoot logoutURL={logoutURL}>
        {children}
      </AuthenticatedContentRoot>
    </Auth0Provider>
  );
}
